import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '../useToast';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { mixpanelEvents } from '@/constants/mixpanel';
import { inviteUsersToOrganizationPost } from '@/services/api/requests/organization';
import { TCustomAxiosError } from '@/services/api';
import queryKeys from '@/constants/queryKeys';
import { TUserInviteInfo } from '@/types/User.types';
import { getInviteFailures } from '@/utils/inviteFailures';

const useAddMemberToOrganization = (onSuccessCallback?: () => void) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();

  const { isPending, mutate, data, error, reset } = useMutation({
    mutationFn: async (usersToInvite: TUserInviteInfo[]) => {
      const response = await inviteUsersToOrganizationPost(usersToInvite);

      return response;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });

      if (result.failed.length > 0) return;

      onSuccessCallback?.();

      toast({
        status: 'success',
        title: 'Success',
        description: 'Member invitation successful!',
      });

      trackEvent(mixpanelEvents.ADD_ORG_MEMBER);

      return;
    },
    onError: (error: TCustomAxiosError) => {
      if (getInviteFailures(error).length > 0) return;
      toast({
        title: 'Error while adding member to organization',
      }, error);
    },
  });

  return { isPending, mutate, data, error, reset };
};

export default useAddMemberToOrganization;
